import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CopilotPanel from "@/components/CopilotPanel";
import OutreachModal from "@/components/OutreachModal";
import IntegrationTab from "@/components/IntegrationTab";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import {
  ArrowLeft, Sparkle, Plus, ShieldCheck, Warning,
  CloudArrowDown, EnvelopeSimple, Crosshair, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDateFr } from "@/components/company/helpers";
import SyntheseTab from "@/components/company/SyntheseTab";
import DueDiligenceTab from "@/components/company/DueDiligenceTab";
import CrmTab from "@/components/company/CrmTab";
import ContactsTab from "@/components/company/ContactsTab";

const TABS = ["Synthèse", "Due Diligence", "Intégration", "CRM", "Contacts"];

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("Synthèse");
  const [copilot, setCopilot] = useState(false);
  const [outreach, setOutreach] = useState(false);
  const [outreachPrefill, setOutreachPrefill] = useState("");

  const load = () => api.get(`/companies/${id}`).then((r) => setC(r.data));
  useEffect(() => { load(); }, [id]);

  const addToPipeline = async () => {
    try { await api.post("/deals", { company_id: id }); toast.success("Ajouté au pipeline M&A"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const [enriching, setEnriching] = useState(false);
  const enrich = async () => {
    setEnriching(true);
    try { await api.post(`/companies/${id}/enrich-pappers`); await load(); toast.success("Données financières mises à jour (Pappers)"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur Pappers"); }
    finally { setEnriching(false); }
  };
  const [enriching360, setEnriching360] = useState(false);
  const enrich360 = async () => {
    setEnriching360(true);
    try {
      const { data } = await api.post(`/companies/${id}/enrich-360`);
      await load();
      const sig = data.transmission_signal;
      toast.success(`Triangulation 360° effectuée · Signal de transmission : ${sig?.niveau || "—"} (${sig?.score ?? "—"}/100)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur enrichissement 360°"); }
    finally { setEnriching360(false); }
  };
  const [findingContacts, setFindingContacts] = useState(false);
  const deleteCompany = async () => {
    if (!window.confirm(`Supprimer définitivement le cabinet « ${c?.nom} » ? Les deals, intégrations, analyses et activités liés seront aussi supprimés. Action irréversible.`)) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Cabinet supprimé");
      window.close();
      navigate("/sourcing");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la suppression"); }
  };
  const findContacts = async () => {
    setFindingContacts(true);
    try {
      const { data } = await api.post(`/companies/${id}/find-contacts`, {});
      await load();
      if (data.found) {
        const ct = data.contacts;
        toast.success(`Contacts trouvés sur ${ct.site} · ${ct.emails.length} email(s), ${ct.telephones.length} tél.`);
      } else {
        toast.warning(data.message || "Aucun contact trouvé automatiquement.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur recherche de contacts"); }
    finally { setFindingContacts(false); }
  };

  if (!c) return <div className="p-8 text-muted-foreground">Chargement…</div>;

  return (
    <div>
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground"><ArrowLeft size={20} /></button>
          <div className="min-w-0">
            <h1 className="font-head font-bold text-xl truncate">{c.nom}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-xs text-muted-foreground font-mono">SIREN {c.siren} · ORIAS {c.orias}</div>
              <select data-testid="type-activite-select" value={c.type_activite || "inconnu"}
                title={c.type_source === "orias" ? "Source ORIAS (officiel)" : c.type_source === "manuel" ? "Corrigé manuellement" : "Type estimé (heuristique) — corrige-le si besoin"}
                onChange={async (e) => {
                  try { await api.patch(`/companies/${id}`, { type_activite_manual: e.target.value }); toast.success("Type d'activité mis à jour"); load(); }
                  catch { toast.error("Erreur mise à jour du type"); }
                }}
                onClick={(e) => e.stopPropagation()}
                className="text-[11px] font-semibold border border-border bg-white px-1.5 py-0.5 rounded-full outline-none focus:border-primary cursor-pointer">
                <option value="courtier">Courtier</option>
                <option value="agent_general">Agent général</option>
                <option value="mandataire">Mandataire</option>
                <option value="inconnu">Inconnu</option>
              </select>
              {c.type_source === "heuristique" && <span className="text-[10px] text-amber-600">estimé</span>}
              {c.type_source === "orias" && <span className="text-[10px] text-emerald-600">✓ ORIAS</span>}
              <button data-testid="orias-verify-btn" onClick={async (e) => {
                e.stopPropagation();
                try {
                  const { data } = await api.post(`/companies/${id}/orias-verify`);
                  if (data.orias_found) toast.success(`ORIAS : ${data.category_labels.join(", ")} (n° ${data.registration_number})`);
                  else toast.info("Non trouvé au registre ORIAS");
                  load();
                } catch (err) { toast.error(err.response?.data?.detail || "Erreur ORIAS"); }
              }}
                className="text-[11px] font-semibold border border-primary text-primary px-1.5 py-0.5 rounded-full hover:bg-accent inline-flex items-center gap-1">
                <ShieldCheck size={11} /> Vérifier ORIAS
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="contact-btn" onClick={() => setOutreach(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800">
            <EnvelopeSimple size={15} /> Contacter
          </button>
          <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid="enrich-pappers-btn" onClick={enrich} disabled={enriching}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-border hover:bg-secondary disabled:opacity-50">
                <CloudArrowDown size={15} /> {enriching ? "Enrichissement…" : "Enrichir"}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-snug">
              Récupère les <b>données financières réelles</b> (CA, résultat, dirigeants) depuis Pappers.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid="enrich-360-btn" onClick={enrich360} disabled={enriching360}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold bg-foreground text-white hover:opacity-90 disabled:opacity-50">
                <Crosshair size={15} /> {enriching360 ? "Triangulation…" : "360°"}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[280px] text-xs leading-snug">
              <b>Triangulation 360°</b> : croise plusieurs sources officielles (Recherche-entreprises, BODACC, Pappers) et calcule le <b>signal de transmission</b> du cabinet.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid="find-contacts-btn" onClick={findContacts} disabled={findingContacts}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-border hover:bg-secondary disabled:opacity-50">
                <EnvelopeSimple size={15} /> {findingContacts ? "Recherche…" : "Contacts"}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-snug">
              Trouve automatiquement le <b>site web, les emails et téléphones</b> du cabinet et de ses dirigeants.
            </TooltipContent>
          </Tooltip>
          </TooltipProvider>
          <button data-testid="company-copilot-btn" onClick={() => setCopilot(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold bg-[#EEF2FF] text-[#4f46e5] border border-[#c7d2fe] hover:bg-[#e0e7ff]">
            <Sparkle size={15} weight="fill" /> Copilot
          </button>
          <button data-testid="add-pipeline-btn" onClick={addToPipeline}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-primary text-primary hover:bg-accent">
            <Plus size={15} /> Pipeline
          </button>
          {user?.role === "super_admin" && (
            <button data-testid="delete-company-btn" onClick={deleteCompany} title="Supprimer ce cabinet"
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-red-300 text-red-700 hover:bg-red-50">
              <Trash size={15} />
            </button>
          )}
        </div>
      </header>

      <div className="px-8 pt-4 border-b border-border bg-white sticky top-16 z-10">
        <div className="flex gap-6">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="p-8">
        {c.radie && (
          <div data-testid="radiation-banner" className="mb-6 flex items-start gap-3 border-l-4 border-red-600 bg-red-50 px-4 py-3">
            <Warning size={20} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-head font-bold text-red-700 text-sm">
                Cabinet radié{c.date_radiation ? ` le ${fmtDateFr(c.date_radiation)}` : " / cessé"}
              </div>
              <div className="text-xs text-red-600/90 mt-0.5">
                Cette entité n'est plus active (radiation BODACC ou état administratif « Cessé »). Ce n'est plus une cible acquérable — vérifiez avant toute action.
              </div>
            </div>
          </div>
        )}
        {tab === "Synthèse" && <SyntheseTab c={c} reload={load} onUseInEmail={(msg) => { setOutreachPrefill(msg); setOutreach(true); }} />}
        {tab === "Due Diligence" && <DueDiligenceTab cid={id} />}
        {tab === "Intégration" && <IntegrationTab cid={id} />}
        {tab === "CRM" && <CrmTab cid={id} companyNom={c.nom} />}
        {tab === "Contacts" && <ContactsTab cid={id} />}
      </div>

      <CopilotPanel open={copilot} onClose={() => setCopilot(false)} companyId={id} companyName={c.nom} />
      {outreach && <OutreachModal company={c} initialBody={outreachPrefill} onClose={() => { setOutreach(false); setOutreachPrefill(""); }} onLogged={load} />}
    </div>
  );
}
